// admin/dashboard/AdminDashboard.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  EyeOff,
  Route,
  Users,
  Shield,
  TrendingUp,
  TrendingDown,
  AlertCircle,
} from "lucide-react";
import { fetchAdminStats } from "@/store/slices/adminSlice";

/**
 * StatCard Component
 * Displays a single statistic with icon, title, value, and optional trend
 */
const StatCard = ({
  icon: Icon,
  title,
  value,
  description,
  trend,
  color = "blue",
}) => {
  const colorClasses = {
    blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
    green:
      "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    yellow:
      "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400",
    red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
    purple:
      "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400",
    gray: "bg-gray-50 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400",
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {title}
              </p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {value}
              </h3>
            </div>
          </div>
          {description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {description}
            </p>
          )}
        </div>
        {trend && (
          <div
            className={`flex items-center gap-1 text-xs font-medium ${
              trend > 0
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {trend > 0 ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <TrendingDown className="w-4 h-4" />
            )}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { stats, isLoadingStats, statsError } = useSelector(
    (state) => state.admin
  );
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (user?.role === "moderator") {
      let targetPath = null;
      if (user?.moderator_permissions?.length > 0) {
        const firstPerm = user.moderator_permissions[0];
        if (firstPerm === "user_management") targetPath = "/admin/users";
        else if (firstPerm === "route_management") targetPath = "/admin/routes";
        else if (firstPerm === "organizer_management") targetPath = "/admin/organizers";
        else if (firstPerm === "sponsor_management") targetPath = "/admin/sponsors";
      }
      if (targetPath) {
        navigate(targetPath, { replace: true });
        return;
      }
    }

    // Fetch stats when component mounts
    if (user?.role === "admin") {
      dispatch(fetchAdminStats());
    }
  }, [dispatch, user, navigate]);

  // Extract stats from the response
  const routeStats = stats?.routes || {
    pending: 0,
    approved: 0,
    rejected: 0,
    hidden: 0,
    total: 0,
  };

  const userStats = stats?.users || {
    total: 0,
    admins: 0,
  };

  // Loading state
  if (isLoadingStats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">
            Loading dashboard statistics...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (statsError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="bg-red-50 dark:bg-red-900/20 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Failed to Load Statistics
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">{statsError}</p>
          <button
            onClick={() => dispatch(fetchAdminStats())}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Dashboard
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Overview of routes and user statistics
        </p>
      </div>

      {/* Route Statistics Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Route Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard
            icon={Clock}
            title="Pending Routes"
            value={routeStats.pending}
            description="Routes waiting for admin approval"
            color="yellow"
          />
          <StatCard
            icon={CheckCircle}
            title="Approved Routes"
            value={routeStats.approved}
            description="Routes that have been approved by admins"
            color="green"
          />
          <StatCard
            icon={XCircle}
            title="Rejected Routes"
            value={routeStats.rejected}
            description="Routes that were rejected by admins"
            color="red"
          />
          <StatCard
            icon={EyeOff}
            title="Hidden Routes"
            value={routeStats.hidden}
            description="Routes that are hidden from public view"
            color="gray"
          />
          <StatCard
            icon={Route}
            title="Total Routes"
            value={routeStats.total}
            description="Total number of all routes in the system"
            color="blue"
          />
        </div>
      </div>

      {/* User and Admin Statistics Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          User and Admin Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            icon={Users}
            title="Total Users"
            value={userStats.total}
            description="Total number of registered users"
            color="purple"
          />
          <StatCard
            icon={Shield}
            title="Total Admins"
            value={userStats.admins}
            description="Number of users with admin privileges"
            color="blue"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
